import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API, PortalProvider, initials, useHeaders } from '../clientportal/kit';
import { NotificationsPanel } from '../clientportal/NotificationsPanel';
import { usePortal } from '../clientportal/kit';
import '../../styles/client-portal.css';
import '../../styles/client-portal-app.css';

const ICONS = {
  overview: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>',
  cleans: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4m10-4v4M3 11h18"/>',
  properties: '<path d="M3 10l9-7 9 7M5 9v12h14V9M9 21v-8h6v8"/>',
  messages: '<path d="M21 15a3 3 0 0 1-3 3H8l-5 3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z"/>',
  billing: '<path d="M5 3h14v18l-3-2-4 2-4-2-3 2zM8 8h8M8 12h8"/>',
  account: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/>',
};
const GURU_HEAD = '<image href="/email-assets/guru-head-bold-v2.png" width="3500" height="3500"/>';
export const CLIENT_NAV = [
  ['overview', 'Overview', '/portal/dashboard'], ['cleans', 'My Cleans', '/portal/appointments'],
  ['properties', 'My Properties & Intake', '/portal/properties'], ['messages', 'Messages', '/portal/messages'],
  ['billing', 'Billing', '/portal/invoices'], ['account', 'Account', '/portal/profile'],
];
const MATCH = { cleans: ['/portal/appointments', '/portal/reports', '/portal/change-requests'] };
const activeKey = (path) => (CLIENT_NAV.find(([k, , to]) => path.startsWith(to) || (MATCH[k] || []).some((m) => path.startsWith(m))) || CLIENT_NAV[0])[0];

const Avatar = ({ user, light, ...p }) => (
  <span className={`avatar${light ? ' light' : ''}`} {...p}>{user?.photo_url ? <img src={user.photo_url} alt="Your profile" /> : initials(user?.full_name)}</span>
);

const GuruBell = ({ dot }) => {
  const { openDrawer } = usePortal();
  return (
    <button className="notification" aria-label="Notifications" data-testid="client-notifications-btn"
      onClick={() => openDrawer({ title: 'Updates from your team', label: 'NOTIFICATIONS', content: <NotificationsPanel /> })}>
      <svg viewBox="330 260 3027 2963" aria-hidden="true" dangerouslySetInnerHTML={{ __html: GURU_HEAD }} />
      {dot && <i data-testid="client-notification-dot" />}
    </button>
  );
};

const useUnread = (pathname) => {
  const headers = useHeaders();
  const [u, setU] = useState({ messages: 0, notes: 0 });
  useEffect(() => {
    Promise.all([axios.get(`${API}/api/portal/messages/unread`, { headers }), axios.get(`${API}/api/notifications/unread-count`, { headers })])
      .then(([m, n]) => setU({ messages: m.data.unread, notes: n.data.unread_count ?? n.data.count ?? 0 })).catch(() => {});
  }, [headers, pathname]);
  return u;
};

// Approved client portal frame (client-portal-review-v1): charcoal sidebar, warm canvas, slide-over menu on phones
export const ClientShell = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const key = activeKey(pathname);
  const unread = useUnread(pathname);
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { document.title = `${CLIENT_NAV.find((n) => n[0] === key)[1]} | Gleam Guru Client Portal`; }, [key]);
  return (
    <div className={`gcp${open ? ' nav-open' : ''}`} data-testid="client-shell">
      <PortalProvider>
        {open && <button id="scrim" aria-label="Close navigation" onClick={() => setOpen(false)} data-testid="client-scrim" />}
        <aside id="sidebar">
          <Link className="brand" to="/portal/dashboard" aria-label="Client overview"><img src="/email-assets/gleam-guru-logo.png" alt="Gleam Guru Cleaners" /></Link>
          <div className="workspace-label"><i /> YOUR CLIENT PORTAL</div>
          <nav aria-label="Client navigation">
            {CLIENT_NAV.map(([k, label, to]) => (
              <Link key={k} to={to} className={`nav${k === key ? ' active' : ''}`} aria-current={k === key ? 'page' : undefined} data-testid={`client-nav-${k}`}>
                <svg viewBox="0 0 24 24" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICONS[k] }} />{label}{k === 'messages' && unread.messages > 0 && <b data-testid="client-nav-unread">{unread.messages}</b>}
              </Link>
            ))}
          </nav>
          <div className="side-bottom">
            <Link to="/portal/messages" className="help" style={{ display: 'block' }} data-testid="client-help">Need a hand? <span>Talk with your team ↗</span></Link>
            <div className="identity"><Avatar user={user} /><div><strong data-testid="client-sidebar-name">{user?.full_name}</strong><small>Gleam Guru client</small></div></div>
          </div>
        </aside>
        <div className="shell">
          <header>
            <div className="breadcrumb">
              <button id="menu" aria-label="Open navigation" aria-expanded={open} onClick={() => setOpen(true)} data-testid="client-menu-btn">☰</button>
              <span>My workspace</span><span>/</span><strong data-testid="client-crumb">{CLIENT_NAV.find((n) => n[0] === key)[1]}</strong>
            </div>
            <div className="top-actions">
              <GuruBell dot={unread.messages > 0 || unread.notes > 0} />
              <Link to="/portal/profile" aria-label="My account" data-testid="client-topbar-account" style={{ textDecoration: 'none' }}><Avatar user={user} light /></Link>
            </div>
          </header>
          <main id="content" tabIndex={-1}><div className="page-enter" key={pathname}>{children}</div></main>
          <footer>GLEAM GURU CLEANERS <span>Your client portal · Times shown in Pacific time</span></footer>
        </div>
      </PortalProvider>
    </div>
  );
};

export default ClientShell;
